// Activity Service for Firestore Operations
import {
    collection,
    doc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp,
    onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Activity, ActivityType } from '../models/index';

export class ActivityService {
    private static getCollectionPath(tripId: string) {
        return `trips/${tripId}/activities`;
    }

    private static convertTimestamps(data: any): any {
        const converted = { ...data };
        if (data.date instanceof Timestamp) {
            converted.date = data.date.toDate();
        }
        if (data.endDate instanceof Timestamp) {
            converted.endDate = data.endDate.toDate();
        }
        if (data.createdAt instanceof Timestamp) {
            converted.createdAt = data.createdAt.toDate();
        }
        if (data.updatedAt instanceof Timestamp) {
            converted.updatedAt = data.updatedAt.toDate();
        }
        return converted;
    }

    // Get all activities for a trip
    static async getActivities(tripId: string): Promise<Activity[]> {
        try {
            console.log('🔍 [ActivityService] Getting activities for trip:', tripId);
            const activitiesRef = collection(db, this.getCollectionPath(tripId));
            // Simplified query without orderBy to avoid composite index requirements
            const snapshot = await getDocs(activitiesRef);

            console.log('✅ [ActivityService] Found', snapshot.size, 'activities');
            const activities = snapshot.docs.map((doc, index) => {
                const data = doc.data();
                const activity = {
                    id: doc.id,
                    tripId,
                    ...this.convertTimestamps(data),
                } as Activity;

                // Debug: Log all fields for first activity to see structure
                if (index === 0) {
                    console.log('🔍 [ActivityService] First activity fields:', Object.keys(data));
                    console.log('🔍 [ActivityService] First activity sample:', {
                        title: data.title,
                        location: data.location,
                        regions: data.regions,
                    });
                }

                // Debug: Log location data
                if (data.location) {
                    console.log('📍 [ActivityService] Activity location:', activity.title || activity.id, data.location);
                } else if (data.regions && data.regions.length > 0) {
                    console.log('📍 [ActivityService] Activity has regions (no location):', activity.title || activity.id, data.regions[0]);
                }

                return activity;
            });

            // Sort in memory by date and order
            activities.sort((a, b) => {
                const dateA = a.date ? new Date(a.date).getTime() : 0;
                const dateB = b.date ? new Date(b.date).getTime() : 0;
                if (dateA !== dateB) return dateA - dateB;
                return (a.order || 0) - (b.order || 0);
            });

            return activities;
        } catch (error) {
            console.error('❌ [ActivityService] Error getting activities:', error);
            throw error;
        }
    }

    // Subscribe to activities
    static subscribeToActivities(
        tripId: string,
        callback: (activities: Activity[]) => void
    ): () => void {
        console.log('🔍 [ActivityService] Setting up activity subscription for trip:', tripId);
        const activitiesRef = collection(db, this.getCollectionPath(tripId));
        // Simplified query without orderBy to avoid composite index requirements

        return onSnapshot(
            activitiesRef,
            (snapshot) => {
                console.log('📡 [ActivityService] Activity update:', snapshot.size, 'activities');
                const activities = snapshot.docs.map((doc, index) => {
                    const data = doc.data();
                    const activity = {
                        id: doc.id,
                        tripId,
                        ...this.convertTimestamps(data),
                    } as Activity;

                    // Debug: Log all fields for first activity
                    if (index === 0) {
                        console.log('🔍 [ActivityService] First activity fields:', Object.keys(data));
                        console.log('🔍 [ActivityService] First activity sample:', {
                            title: data.title,
                            location: data.location,
                            regions: data.regions,
                        });
                    }

                    // Debug: Log location data
                    if (data.location) {
                        console.log('📍 [ActivityService] Activity HAS location:', activity.title || activity.id, data.location);
                    } else if (data.regions && data.regions.length > 0) {
                        console.log('📍 [ActivityService] Activity has regions (no location):', activity.title || activity.id, data.regions[0]);
                    }

                    return activity;
                });

                // Sort in memory by date and order
                activities.sort((a, b) => {
                    const dateA = a.date ? new Date(a.date).getTime() : 0;
                    const dateB = b.date ? new Date(b.date).getTime() : 0;
                    if (dateA !== dateB) return dateA - dateB;
                    return (a.order || 0) - (b.order || 0);
                });

                callback(activities);
            },
            (error) => {
                console.error('❌ [ActivityService] Error in activity subscription:', error);
            }
        );
    }

    // Create activity
    static async createActivity(
        tripId: string,
        activity: Omit<Activity, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>
    ): Promise<string> {
        try {
            const activityRef = doc(collection(db, this.getCollectionPath(tripId)));
            // Strip undefined fields — Firestore rejects them with "Unsupported field value: undefined"
            const activityData = Object.fromEntries(
                Object.entries({
                    ...activity,
                    tripId,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                }).filter(([, v]) => v !== undefined)
            );
            await setDoc(activityRef, activityData);
            return activityRef.id;
        } catch (error) {
            console.error('Error creating activity:', error);
            throw error;
        }
    }

    // Update activity
    static async updateActivity(
        tripId: string,
        activityId: string,
        updates: Partial<Activity>
    ): Promise<void> {
        try {
            const activityRef = doc(db, this.getCollectionPath(tripId), activityId);
            await updateDoc(activityRef, {
                ...updates,
                updatedAt: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error updating activity:', error);
            throw error;
        }
    }

    // Delete activity
    static async deleteActivity(tripId: string, activityId: string): Promise<void> {
        try {
            await deleteDoc(doc(db, this.getCollectionPath(tripId), activityId));
        } catch (error) {
            console.error('Error deleting activity:', error);
            throw error;
        }
    }

    // Toggle activity selection
    static async toggleSelection(tripId: string, activityId: string, isSelected: boolean): Promise<void> {
        await this.updateActivity(tripId, activityId, { isSelected });
    }

    // Update activity order
    static async reorderActivities(tripId: string, activityIds: string[]): Promise<void> {
        try {
            const batch = activityIds.map((id, index) =>
                this.updateActivity(tripId, id, { order: index })
            );
            await Promise.all(batch);
        } catch (error) {
            console.error('Error reordering activities:', error);
            throw error;
        }
    }

    // Filter activities
    static filterActivities(
        activities: Activity[],
        filters: {
            date?: Date;
            type?: ActivityType;
            regionId?: string;
            isSelected?: boolean;
        }
    ): Activity[] {
        return activities.filter((activity) => {
            if (filters.date && activity.date) {
                const activityDate = new Date(activity.date).toDateString();
                const filterDate = new Date(filters.date).toDateString();
                if (activityDate !== filterDate) return false;
            }
            if (filters.type && activity.type !== filters.type) return false;
            if (filters.regionId && !activity.regions.some((r) => r.id === filters.regionId)) return false;
            if (filters.isSelected !== undefined && activity.isSelected !== filters.isSelected) return false;
            return true;
        });
    }

    // Group activities by date
    static groupByDate(activities: Activity[]): Map<string, Activity[]> {
        const grouped = new Map<string, Activity[]>();

        activities.forEach((activity) => {
            if (activity.date) {
                const dateKey = new Date(activity.date).toDateString();
                if (!grouped.has(dateKey)) {
                    grouped.set(dateKey, []);
                }
                grouped.get(dateKey)!.push(activity);
            } else {
                // Unscheduled activities
                if (!grouped.has('unscheduled')) {
                    grouped.set('unscheduled', []);
                }
                grouped.get('unscheduled')!.push(activity);
            }
        });

        return grouped;
    }
}

export default ActivityService;
